import { Controller } from "@hotwired/stimulus"

interface RaceItem {
  id: number
  title: string
  price_cents: number
  max_quantity?: number
  attribute_1_title?: string
  attribute_1_options?: string[]
  attribute_2_title?: string
  attribute_2_options?: string[]
}

export default class extends Controller {
  static targets = ["template", "container", "selector", "item"]
  static values = {
    items: Array
  }

  declare templateTarget: HTMLTemplateElement
  declare containerTarget: HTMLElement
  declare selectorTarget: HTMLSelectElement
  declare itemTargets: HTMLElement[]
  declare itemsValue: RaceItem[]

  add(event: Event): void {
    event.preventDefault()

    const selectedId = this.selectorTarget.value
    if (selectedId === "?") {
      alert("Please select an item.")
      return
    }

    const item = this.itemsValue.find(i => String(i.id) === selectedId)
    if (!item) return

    const timestamp = new Date().getTime()
    const content = this.templateTarget.innerHTML
      .replace(/NEW_RECORD/g, String(timestamp))
      .replace(/ITEM_ID/g, String(item.id))
      .replace(/ITEM_TITLE/g, item.title)
      .replace(/ITEM_PRICE/g, (item.price_cents / 100.0).toFixed(2))

    // Create a temporary container to parse the HTML
    const temp = document.createElement("div")
    temp.innerHTML = content
    const itemElement = temp.firstElementChild
    if (!itemElement) return

    // Handle attribute 1 options
    const attr1Container = itemElement.querySelector("[data-attr1-container]")
    if (attr1Container) {
      if (item.attribute_1_options && item.attribute_1_options.length > 0) {
        const label = attr1Container.querySelector("[data-attr1-label]")
        const select = attr1Container.querySelector("select")
        if (label) label.textContent = item.attribute_1_title ?? null
        if (select) {
          select.innerHTML = "<option></option>" +
            item.attribute_1_options.map(opt => `<option value="${opt}">${opt}</option>`).join("")
        }
      } else {
        attr1Container.remove()
      }
    }

    // Handle attribute 2 options
    const attr2Container = itemElement.querySelector("[data-attr2-container]")
    if (attr2Container) {
      if (item.attribute_2_options && item.attribute_2_options.length > 0) {
        const label = attr2Container.querySelector("[data-attr2-label]")
        const select = attr2Container.querySelector("select")
        if (label) label.textContent = item.attribute_2_title ?? null
        if (select) {
          select.innerHTML = "<option></option>" +
            item.attribute_2_options.map(opt => `<option value="${opt}">${opt}</option>`).join("")
        }
      } else {
        attr2Container.remove()
      }
    }

    // Handle quantity options
    const quantitySelect = itemElement.querySelector<HTMLSelectElement>("[data-quantity-select]")
    if (quantitySelect) {
      const maxQty = item.max_quantity || 10
      quantitySelect.innerHTML = Array.from({length: maxQty}, (_, i) =>
        `<option value="${i + 1}">${i + 1}</option>`
      ).join("")
    }

    // Insert the element
    this.containerTarget.appendChild(itemElement)

    // Show the container if it was hidden
    this.containerTarget.classList.remove("hidden")

    // Reset selector
    this.selectorTarget.value = "?"
  }

  remove(event: Event): void {
    event.preventDefault()
    const item = (event.target as HTMLElement).closest("[data-race-items-target='item']")
    if (item) {
      item.remove()
    }
  }
}
